import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ArrowForward, InfoOutline } from '@mui/icons-material';
import { Stack, Typography, Card, Box, Switch, Tooltip, Grid } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { DialogContext } from '@cvt/contexts/DialogContext/DialogContext';

import config from '@shared/config';

import PetDetailsQuickAddView from '@modules/Tributes/components/PetDetailsQuickAddView';
import { PetDetailsManualForm, FormData as ManualFormData } from '@modules/Tributes/components/PetDetailsManualForm';
import { FormData as QuickAddFormData } from '@modules/Tributes/components/PetDetailsQuickAddView';
import { TributeWizardContext } from '@modules/Tributes/contexts/TributeWizardContext';
import { AuthContext } from '@modules/Auth/contexts';

import { TributeFormData } from '../CreateTribute/CreateTribute';

interface Props {
    onComplete: () => void;
}

export const DetailsStep: FC<Props> = (props) => {
  const { onComplete } = props;
  const [isQuickAddView, setIsQuickAddView] = useState<boolean>(false);
  const { getValues, setValue } = useFormContext<TributeFormData>();
  const { setData: setCachedDetails } = useContext(TributeWizardContext);
  const { openDialog, closeDialog } = useContext(DialogContext);

  const { isLoggedIn } = useContext(AuthContext);

  const defaultFormValues = useMemo(() => {
    const values = getValues();
    return {
      petName: values.petName || '',
      petDescription: values.petDescription || '',
      petDateOfBirth: values.petDateOfBirth || null,
      petDateOfPassing: values.petDateOfPassing || null,
      petType: values.petType || '',
      sex: values.sex || '',
    };
  }, [getValues]);

  const handleQuickAddToggle = useCallback((isChecked: boolean) => {
    setIsQuickAddView(isChecked);
  }, []);

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      setValue(fieldName as keyof TributeFormData, value, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const onSubmitPetDescription = useCallback(
    (data: QuickAddFormData) => {
      handleQuickAddToggle(false);
    },
    [handleQuickAddToggle],
  );

  const onCreateAccount = useCallback(() => {
    openDialog('authentication', {
      step: 'signup',
      onAuthenticationSuccess: async () => {
        closeDialog('authentication');
        onComplete();
      },
    });
  }, [openDialog, closeDialog, onComplete]);

  const onSubmitManualForm = useCallback(
    async (data: ManualFormData) => {
      // Persist only Tribute-related data in the CreateTribute form
      setValue('petName', data.petName || '', { shouldValidate: true });
      setValue('petType', data.petType || '', { shouldValidate: true });
      setValue('sex', data.sex || '', { shouldValidate: true });
      setValue('petDescription', data.petDescription || '', { shouldValidate: true });
      setValue('petDateOfBirth', data.petDateOfBirth || null, { shouldValidate: true });
      setValue('petDateOfPassing', data.petDateOfPassing || null, { shouldValidate: true });
      setValue('firstName', data.firstName || '', { shouldValidate: true });
      setValue('email', data.email || '', { shouldValidate: true });
      setValue('phone', data.phone || '', { shouldValidate: true });

      setCachedDetails('details', data);

      if (isLoggedIn) {
        onComplete();
      } else {
        onCreateAccount();
      }
    },
    [onComplete, onCreateAccount, setCachedDetails, setValue, isLoggedIn],
  );

  return (
    <Stack sx={{ pt: 2, px: { xs: 2, sm: 0 } }}>
      <Typography variant="h6" component="h2" align="center" fontSize={20} fontWeight={600}>
                Pet Details
      </Typography>

      <Typography variant="body2" color="textSecondary" align="center" fontSize={16} mb={2}>
        Tell us about your beloved companion
      </Typography>

      <Card
        sx={{
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgb(254, 238, 237)',
          borderRadius: '12px',
          overflow: 'visible',
          py: 2,
          px: 2,
        }}
      >
        {config.featureFlags.quickAddMode && (
          <Stack
            sx={{
              justifyContent: 'center',
              backgroundImage: 'linear-gradient(to right bottom, rgb(250, 246, 251), rgb(239, 246, 255))',
              border: '1px solid rgb(231, 213, 235)',
              p: 3,
              borderRadius: '12px',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexGrow={1}
              sx={{
                bgcolor: 'rgb(239, 246, 255)',
                border: '1px solid rgb(191, 219, 254)',
                p: 2,
                borderRadius: '12px',
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Switch
                  checked={isQuickAddView}
                  onChange={({ target: { checked } }) => handleQuickAddToggle(checked)}
                  color="primary"
                />
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, mr: 1 }}>
                                    Quick Add Mode
                </Typography>
              </Stack>

              <Tooltip
                title="Quick add allows you to quickly enter your pet's information without answering all questions"
                placement="top"
              >
                <InfoOutline fontSize="small" sx={{ color: 'rgb(37, 99, 235)' }} />
              </Tooltip>
            </Stack>

            {isQuickAddView && (
              <PetDetailsQuickAddView
                defaultValues={{ petDescription: defaultFormValues?.petDescription || '' }}
                onFieldChange={handleFieldChange}
                onSubmitRequest={onSubmitPetDescription}
                onToggleQuickAddMode={handleQuickAddToggle}
              />
            )}
          </Stack>
        )}

        <Box
          sx={{
            mt: config.featureFlags.quickAddMode ? 3 : 0,
            p: { xs: 2, sm: 3 },
            border: '1px solid #e6e6e8',
            borderRadius: '12px',
          }}
        >
          {!isQuickAddView && (
            <PetDetailsManualForm
              defaultValues={defaultFormValues}
              onFieldChange={handleFieldChange}
              onSubmitRequest={onSubmitManualForm}
              slots={{
                footer: (formState) => (
                  <Grid>
                    <Grid size={12}>
                      <Stack direction="row" alignItems="center" mt={2}>
                        <LoadingButton
                          variant="contained"
                          color="primary"
                          type="submit"
                          loading={formState.isSubmitting}
                          disabled={formState.isSubmitting}
                          sx={{
                            px: 3,
                            py: 1,
                          }}
                          endIcon={<ArrowForward />}
                        >
                          Continue
                        </LoadingButton>
                      </Stack>
                    </Grid>
                  </Grid>
                ),
              }}
            />
          )}
        </Box>
      </Card>
    </Stack>
  );
};
